/**
 * One sim step: mine, haul, smelt, craft. Pure state mutation — no DOM, no
 * wall clock — so the same code runs live and for offline catch-up.
 */

import { PLANETS } from '../content/planets';
import { DISCOVERY_SC, resource, type Ingredient } from '../content/resources';
import { cargoCap, craftSpeed, craftYield, legTime, mineRate, smeltSpeed, smeltYield } from './economy';
import { stepAsteroids } from './asteroids';
import { stepRover } from './rovers';
import { stepChallenge, withBackgroundRun } from './challenges';
import { stepShips } from './ships';
import { fleetStats, type FleetStats } from './stats';
import { stepContracts } from './market';
import { recipeInputs } from './rooms';
import type { Emit } from './events';
import type { GameState, SlotState } from './state';

/** Loop guard for legs and recipes completed inside one step. */
const MAX_LOOPS = 64;

/** First time an id is held it is discovered; a first craft of an item pays SC. */
function discover(state: GameState, id: string, emit: Emit): void {
  if (state.discovered.includes(id)) return;
  state.discovered.push(id);
  const def = resource(id);
  const sc = def.kind === 'item' ? DISCOVERY_SC[def.rarity!] : 0;
  state.sc += sc;
  emit({ kind: 'discovered', id, sc });
}

export function addToHold(state: GameState, id: string, qty: number, emit: Emit): void {
  state.run.hold[id] = (state.run.hold[id] ?? 0) + qty;
  discover(state, id, emit);
}

export function earnGc(state: GameState, gc: number): void {
  state.run.gc += gc;
  state.run.lifetimeGc += gc;
  state.lifetime.gc += gc;
}

function unload(state: GameState, index: number, load: number, emit: Emit): void {
  for (const ore of PLANETS[index]!.ores) addToHold(state, ore.id, load * ore.share, emit);
}

function stepPlanet(state: GameState, stats: FleetStats, index: number, dt: number, emit: Emit): void {
  const p = state.run.planets[index]!;
  p.stock += mineRate(state, stats, index) * dt;

  const leg = legTime(state, stats, index);
  let time = dt;
  for (let n = 0; n < MAX_LOOPS && time > 0; n += 1) {
    const remaining = (1 - p.shipT) * leg;
    if (time < remaining) {
      p.shipT += time / leg;
      return;
    }
    time -= remaining;
    p.shipT = 0;
    if (p.shipPhase === 'out') {
      p.shipLoad = Math.min(cargoCap(state, stats, index), p.stock);
      p.stock -= p.shipLoad;
      p.shipPhase = 'back';
    } else {
      unload(state, index, p.shipLoad, emit);
      p.shipLoad = 0;
      p.shipPhase = 'out';
    }
  }
}

export function hasInputs(state: GameState, inputs: readonly Ingredient[]): boolean {
  return inputs.every((i) => (state.run.hold[i.id] ?? 0) >= i.qty);
}

/** Whether a recipe could start now, after the Underforge / Dorms cut. */
export function canStart(state: GameState, id: string): boolean {
  return hasInputs(state, recipeInputs(state, id));
}

function stepSlot(state: GameState, slot: SlotState, speed: number, yieldPer: number, dt: number, emit: Emit): void {
  if (!slot.recipe) return;
  const def = resource(slot.recipe);
  const recipe = def.recipe!;
  let work = dt * speed;
  for (let n = 0; n < MAX_LOOPS && work > 0; n += 1) {
    if (!slot.running) {
      const inputs = recipeInputs(state, def.id);
      if (!hasInputs(state, inputs)) return;
      for (const i of inputs) state.run.hold[i.id]! -= i.qty;
      slot.running = true;
      slot.progress = 0;
    }
    const need = recipe.time - slot.progress;
    if (work < need) {
      slot.progress += work;
      return;
    }
    work -= need;
    slot.running = false;
    slot.progress = 0;
    // Yield accumulates deterministically: 25% yield is one bonus unit every fourth completion.
    slot.bonus = (slot.bonus ?? 0) + yieldPer;
    const extra = Math.floor(slot.bonus);
    slot.bonus -= extra;
    addToHold(state, def.id, 1 + extra, emit);
    if (def.kind === 'item') state.lifetime.crafted += 1 + extra;
  }
}

/** One step of every galaxy, plus the systems that belong to the account. */
export function advance(state: GameState, dt: number, emit: Emit): void {
  state.clock += dt;
  advanceRun(state, dt, emit);
  // A Challenge galaxy keeps running while you look at your main one, and vice versa.
  withBackgroundRun(state, () => advanceRun(state, dt, emit));
  stepContracts(state);
  stepRover(state, emit);
  stepChallenge(state, emit);
  stepShips(state, emit);
}

/** One step of the galaxy in state.run. */
function advanceRun(state: GameState, dt: number, emit: Emit): void {
  const run = state.run;
  run.time += dt;
  const stats = fleetStats(state);

  for (let i = 0; i < run.planets.length; i += 1) {
    if (run.planets[i]!.owned) stepPlanet(state, stats, i, dt, emit);
  }

  const smelt = smeltSpeed(state, stats);
  const sy = smeltYield(stats);
  for (const slot of run.smelters) stepSlot(state, slot, smelt, sy, dt, emit);
  const craft = craftSpeed(state, stats);
  const cy = craftYield(stats);
  for (const slot of run.crafters) stepSlot(state, slot, craft, cy, dt, emit);

  stepAsteroids(state, stats, dt);
}
